import {
  groupLabResultsByCategory,
  resolveObservationProvenance,
  shouldExcludeLabObservation,
  type GroupedLabResults,
} from '@liza/fhir-utils';
import { hasActiveFilters, type RecordsListFilters } from '@liza/ui/components/records/RecordsListControls';
import { useCompleteRecordList } from '@liza/ui/hooks/useCompleteRecordList';
import { classifyObservation } from '@liza/ui/utils/observation';
import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTRPC, useTRPCClient } from '@/utils/trpc';

type Result = GroupedLabResults & {
  query: UseInfiniteQueryResult<unknown, Error>;
  /** `true` once every page has landed — until then the categories are built from what has arrived. */
  isComplete: boolean;
};

/**
 * The patient's lab results, grouped by clinical category.
 *
 * Reads the **whole** history rather than a scroll window: grouping across newest-first pages would
 * reshuffle rows and change each row's latest value as pages land. Rendering is progressive — the
 * list is newest-first, so every row's latest value is right from the first page.
 *
 * Device-origin readings are dropped. A connected meter writes thousands of `laboratory`-category
 * glucose readings, and those belong on the care plan's glucose Result, not in a list of draws.
 *
 * Unfiltered, the categories are the full chart — every curated category and member, drawn or not.
 * Once a search or filter is active the server returns a subset, and an undrawn row would read as a
 * gap in the workup rather than as filtered out, so only categories and labs with a match are listed.
 */
export const useLabResultRecords = (medplumPatientId: string, filters: RecordsListFilters): Result => {
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();

  const listOptions = {
    ownership: filters.ownership,
    searchParams: { 'code:text': filters.searchQuery },
    status: filters.status,
  };

  const { items, query, isComplete } = useCompleteRecordList<WithId<Observation>>({
    queryKey: trpc.patient.observation.list.queryKey({
      category: 'labs',
      medplumPatientId,
      options: listOptions,
    }),
    queryFn: async ({ offset, count }) => {
      const res = await trpcClient.patient.observation.list.query({
        category: 'labs',
        medplumPatientId,
        options: listOptions,
        offset,
        count,
      });
      return { items: res.observations, hasMore: res.hasMore };
    },
  });

  const includeUndrawn = !hasActiveFilters(filters);
  const grouped = useMemo(() => {
    const byId = new Map<string, WithId<Observation>>();
    for (const obs of items) {
      if (classifyObservation(obs) !== 'result') continue;
      if (shouldExcludeLabObservation(obs)) continue;
      if (resolveObservationProvenance(obs).origin === 'device') continue;
      byId.set(obs.id, obs);
    }
    return groupLabResultsByCategory([...byId.values()], { includeUndrawn });
  }, [items, includeUndrawn]);

  return { ...grouped, query, isComplete };
};
